/**
 * Downscales an image in the browser before it is uploaded.
 *
 * ── Why this is client-side ──────────────────────────────────────────────────────────
 *
 * There is no image library anywhere in this project — not on the front end, and
 * deliberately not on the back end either: `helpers/imageDimensions.js` reads dimensions out
 * of the file header and `helpers/imageMetadata.js` strips EXIF *without decoding*, both
 * with a comment explaining that adding `sharp`/`jimp` for the job was not worth a native
 * dependency. So the server genuinely cannot resample pixels.
 *
 * `<canvas>` can, and it is a native browser API, so this adds nothing to package.json.
 * Doing it here also means a 12 MB phone photo never crosses the network at all — the upload
 * is fast as a side effect rather than as a separate optimisation.
 *
 * ── What this is NOT ─────────────────────────────────────────────────────────────────
 *
 * It is not a security control and must never be treated as one. A client can post whatever
 * bytes it likes straight to the endpoint, so the server keeps every check it already had:
 * the declared content type, the file's own magic bytes, the byte-size cap from
 * `vlearn_settings`, the `MAX_IMAGE_SIDE` bound, and the EXIF strip. This only spares the
 * ordinary user from being told their holiday photo is too large.
 *
 * ── Why it can return the original file ──────────────────────────────────────────────
 *
 * Every failure path returns the input unchanged rather than throwing: an image that is
 * already small needs no work, and a browser that cannot decode a particular file should
 * still let the user try to upload it and get the server's answer. A resize helper that can
 * block an upload is worse than one that sometimes does nothing.
 */

/** Anything wider or taller than this is scaled down, preserving aspect ratio. */
export const DEFAULT_MAX_DIMENSION = 1600

/**
 * JPEG/WebP quality for the re-encode. 0.85 is the usual point past which the file grows
 * faster than the picture improves.
 */
export const DEFAULT_QUALITY = 0.85

/** The types canvas can reliably read and that the V-Learning cover endpoint accepts. */
const RESIZABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

/**
 * Loads a File into an HTMLImageElement via an object URL.
 *
 * The object URL is revoked in both outcomes — a leaked one pins the whole decoded bitmap in
 * memory for the life of the document, which for a handful of 12 MP uploads is tens of
 * megabytes that never come back.
 */
function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
            URL.revokeObjectURL(url)
            resolve(image)
        }
        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('decode failed'))
        }
        image.src = url
    })
}

/**
 * Shrinks `file` so neither side exceeds `maxDimension`.
 *
 * @param {File} file
 * @param {object} [options]
 * @param {number} [options.maxDimension=1600]
 * @param {number} [options.quality=0.85]
 * @param {string} [options.mimeType] output type; defaults to the input's, except PNG which
 *        becomes JPEG (see below)
 * @returns {Promise<File>} a new File, or the original when resizing is unnecessary,
 *          impossible, or would not help
 */
export async function shrinkImageFile(file, options = {}) {
    const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION
    const quality = typeof options.quality === 'number' ? options.quality : DEFAULT_QUALITY

    if (!file || !RESIZABLE_TYPES.includes(file.type)) return file
    if (typeof document === 'undefined' || typeof URL === 'undefined') return file

    /*
     * The decision is made on DIMENSIONS ALONE, not on byte size.
     *
     * An earlier version also skipped anything under a few hundred kilobytes, which made the
     * behaviour arbitrary: a 2880px 1.2 MB screenshot was shrunk while a 3000px 200 KB
     * graphic was stored at full size, for no reason a user could infer. And the byte gate was
     * protecting against something already handled — re-encoding a flat-colour PNG can make
     * it larger, but the size comparison at the end of this function catches that and returns
     * the original.
     *
     * So the rule is simply: if it is bigger than it will ever be displayed, shrink it.
     */

    let image
    try {
        image = await loadImage(file)
    } catch (error) {
        // An undecodable image is the server's problem to report, not this helper's to hide.
        return file
    }

    const { width, height } = image
    if (!width || !height) return file

    const scale = Math.min(1, maxDimension / Math.max(width, height))
    // Already within bounds: re-encoding would only lose quality for no size benefit.
    if (scale >= 1) return file

    const targetWidth = Math.max(1, Math.round(width * scale))
    const targetHeight = Math.max(1, Math.round(height * scale))

    /*
     * PNG is re-encoded as JPEG unless the caller says otherwise.
     *
     * A photograph saved as PNG is the common case for an over-large upload, and canvas's PNG
     * encoder is lossless — so a 4000px PNG scaled to 1600px can still come out several
     * megabytes. JPEG at 0.85 is a fraction of that. The trade-off is transparency, which a
     * course cover does not use (it is rendered as a filled 16:9 tile).
     */
    const outputType = options.mimeType
        || (file.type === 'image/png' ? 'image/jpeg' : file.type)

    let blob
    try {
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const context = canvas.getContext('2d')
        if (!context) return file

        // `imageSmoothingQuality: 'high'` matters at these ratios: the default bilinear step
        // on a 4x downscale produces visible aliasing on text and fine detail, which a course
        // cover often has.
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = 'high'
        // A white ground under the drawn image, because a transparent PNG re-encoded to JPEG
        // would otherwise composite onto black.
        if (outputType === 'image/jpeg') {
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, targetWidth, targetHeight)
        }
        context.drawImage(image, 0, 0, targetWidth, targetHeight)

        blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, quality))
    } catch (error) {
        // A tainted or over-large canvas throws rather than returning null. Same answer:
        // hand back the original and let the server decide.
        return file
    }

    if (!blob) return file
    /*
     * If the re-encode grew the file, keep the original.
     *
     * This is a real case, not a theoretical one: canvas has no PNG-optimised encoder, so a
     * flat-colour PNG — a logo, a diagram — can come back larger even at fewer pixels. It is
     * also what makes the dimension-only rule above safe.
     */
    if (blob.size >= file.size) return file

    const extension = outputType === 'image/png' ? '.png'
        : outputType === 'image/webp' ? '.webp' : '.jpg'
    const baseName = String(file.name || 'cover').replace(/\.[^.]+$/, '')

    // A real File, not a Blob: the upload posts FormData, and a Blob would arrive without a
    // filename, which the server uses for `original_name`. `lastModified` is carried over so
    // the new file is not spuriously "modified now".
    return new File([blob], `${baseName}${extension}`, {
        type: outputType,
        lastModified: file.lastModified || Date.now()
    })
}

/** Bytes as a short human string, for telling the user what was saved. */
export function formatBytes(bytes) {
    const value = Number(bytes) || 0
    if (value < 1024) return `${value} B`
    if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
    return `${(value / (1024 * 1024)).toFixed(1)} MB`
}
